import React from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ScaleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { FOSOpinion } from '../../types/signals-live';
import { Panel } from '../v3/common/Panel';
import { Badge } from '../ui/badge';
import { Timestamp } from '../ui/timestamp';
import FOSDecisionDetailModal from './FOSDecisionDetailModal';
import { getOmbudsmanDecisionById } from '../../utils/api';

interface FOSOpinionPanelProps {
  fosOpinion: FOSOpinion | null | undefined;
}

const FOSOpinionPanel: React.FC<FOSOpinionPanelProps> = ({ fosOpinion }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [openDecisionId, setOpenDecisionId] = React.useState<string | null>(null);
  const [loadingDecision, setLoadingDecision] = React.useState(false);
  const [decisionDetail, setDecisionDetail] = React.useState<any | null>(null);

  const getOutcomeIcon = (outcome: string) => {
    switch (outcome) {
      case 'upheld':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'not_upheld':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'partially_upheld':
        return <ScaleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'upheld':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'not_upheld':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'partially_upheld':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getOutcomeLabel = (outcome: string) => {
    switch (outcome) {
      case 'upheld':
        return 'Likely Upheld';
      case 'not_upheld':
        return 'Likely Not Upheld';
      case 'partially_upheld':
        return 'Likely Partially Upheld';
      default:
        return 'Unknown';
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-700 bg-green-50 border-green-200';
    if (score >= 0.6) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-red-700 bg-red-50 border-red-200';
  };

  const formatConfidenceScore = (score: number) => {
    return `${Math.round(score * 100)}%`;
  };

  const formatCurrency = (value: number | undefined, currency?: string) => {
    if (typeof value !== 'number') return '';
    const c = (currency || 'GBP').toUpperCase();
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c }).format(value);
    } catch {
      const prefix = c === 'GBP' ? '£' : c === 'EUR' ? '€' : c === 'USD' ? '$' : '';
      return `${prefix}${value.toFixed(0)}`;
    }
  };

  if (!fosOpinion) {
    return (
      <Panel title="Simulated Financial Ombudsman Decision" className="mb-6">
        <div className="text-center py-8">
          <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No FOS opinion available for this signal.</p>
          <p className="text-sm text-gray-500 mt-2">
            FOS opinions are generated when signals have sufficient context and similar decisions are available.
          </p>
        </div>
      </Panel>
    );
  }

  return (
    <>
    <Panel title="Outcome Prediction" className="mb-6">
        {/* Header with prediction outcome */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {getOutcomeIcon(fosOpinion.predicted_outcome || '')}
            <Badge
              variant="outline"
              className={`${getOutcomeColor(fosOpinion.predicted_outcome || '')}`}
              aria-label={`Predicted outcome: ${getOutcomeLabel(fosOpinion.predicted_outcome || '')}`}
            >
              {getOutcomeLabel(fosOpinion.predicted_outcome || '')}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`${getConfidenceColor(fosOpinion.confidence_score || 0)}`}
              aria-label={`Confidence: ${formatConfidenceScore(fosOpinion.confidence_score || 0)}`}
            >
              {formatConfidenceScore(fosOpinion.confidence_score || 0)} confidence
            </Badge>
          </div>
        </div>

        {/* Reasoning */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">FOS Assessment Reasoning</h3>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
              {(() => {
                const text = fosOpinion.reasoning || 'No reasoning provided.';
                const parts = text.split(/(DRN-\d+)/g);
                return parts.map((part, idx) => {
                  if (/^DRN-\d+$/.test(part)) {
                    const href = `https://www.financial-ombudsman.org.uk/decision/${part}`;
                    return (
                      <a key={idx} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {part}
                      </a>
                    );
                  }
                  return <React.Fragment key={idx}>{part}</React.Fragment>;
                });
              })()}
            </p>
            {(fosOpinion.similar_decisions && fosOpinion.similar_decisions.length > 0) && (
              <div className="mt-3 text-xs text-gray-600">
                <span className="font-medium text-gray-800 mr-1">References:</span>
                {fosOpinion.similar_decisions.slice(0, 5).map((d, i) => (
                  <a
                    key={d.decision_id}
                    href={`https://www.financial-ombudsman.org.uk/decision/${d.decision_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline mr-2"
                  >
                    {d.decision_id}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Key Factors */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Decision Factors</h3>
          <div className="space-y-2">
            {(fosOpinion.key_factors || []).map((factor, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span className="text-sm text-gray-700">{factor}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Redress Guidance (if available and outcome is upheld/partially_upheld) */}
        {((fosOpinion.predicted_outcome === 'upheld' || fosOpinion.predicted_outcome === 'partially_upheld') && (fosOpinion as any).redress_guidance) && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Potential Redress Guidance</h3>
            <div className="bg-white rounded-lg p-4 border border-gray-200 text-sm text-gray-700">
              {fosOpinion.redress_evidence?.level === 'weak' && (
                <div className="mb-3 p-2 rounded border border-yellow-300 bg-yellow-50 text-yellow-800 text-xs" title="Limited evidence: fewer than two strong upheld precedents at or above the similarity threshold.">
                  Caution: limited precedent evidence. Treat this guidance as indicative only.
                </div>
              )}
              {(() => {
                const rg: any = (fosOpinion as any).redress_guidance || {};
                const range = rg.monetary_range || {};
                const rangeText = (range.min != null && range.max != null)
                  ? `${formatCurrency(range.min, range.currency)} – ${formatCurrency(range.max, range.currency)}`
                  : undefined;
                return (
                  <>
                    {rangeText && (
                      <div className="mb-2"><span className="font-medium">Typical monetary range:</span> {rangeText}</div>
                    )}
                    {Array.isArray(rg.types) && rg.types.length > 0 && (
                      <div className="mb-2">
                        <span className="font-medium">Common remedies:</span> {rg.types.map((t: string) => t.replace(/_/g,' ')).join(', ')}
                      </div>
                    )}
                    {Array.isArray(rg.precedents) && rg.precedents.length > 0 && (
                      <div className="mb-2">
                        <span className="font-medium">Precedents:</span>{' '}
                        {rg.precedents.slice(0, 3).map((ref: string, idx: number) => (
                          <a
                            key={`${ref}-${idx}`}
                            href={`https://www.financial-ombudsman.org.uk/decision/${ref}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline mr-2"
                          >
                            {ref}
                          </a>
                        ))}
                      </div>
                    )}
                    {rg.notes && (
                      <div className="text-gray-700 whitespace-pre-line">{rg.notes}</div>
                    )}
                    {/* Why shown justification */}
                    <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
                      {(() => {
                        const precedents: string[] = Array.isArray(rg.precedents) ? rg.precedents : [];
                        if (!precedents.length) return null;
                        const sims = precedents.map((ref: string) => {
                          const sd = (fosOpinion.similar_decisions || []).find((d: any) => d.decision_id === ref);
                          const pct = typeof sd?.similarity_score === 'number' ? `${Math.round(sd.similarity_score * 100)}%` : undefined;
                          return { ref, pct };
                        });
                        return (
                          <div className="flex items-start gap-1">
                            <span className="font-medium text-gray-800">Why shown:</span>{' '}
                            {getOutcomeLabel(fosOpinion.predicted_outcome || '')} with strong precedent in{' '}
                            {sims.map(({ ref, pct }, i) => (
                              <span key={ref}>
                                <a
                                  href={`https://www.financial-ombudsman.org.uk/decision/${ref}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {ref}
                                </a>
                                {pct ? ` (${pct} similar)` : ''}{i < sims.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                            .
                            <InformationCircleIcon
                              className="ml-1 h-4 w-4 text-gray-400"
                              title="Shown only when outcome is upheld/partially upheld AND at least two similar FOS decisions (≥ 65% similarity) support the redress guidance."
                              aria-label="Redress guidance shown criteria"
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Similar Decisions (if available) */}
        {fosOpinion.similar_decisions && fosOpinion.similar_decisions.length > 0 && (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-between w-full text-left"
              aria-expanded={isExpanded}
              aria-controls="fos-similar-decisions"
            >
              <h3 className="text-sm font-semibold text-gray-900">
                Precedent Cases ({fosOpinion.similar_decisions.length})
              </h3>
              {isExpanded ? (
                <ChevronUpIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              )}
            </button>
            
            {isExpanded && (
              <div id="fos-similar-decisions" className="mt-3 space-y-3">
                {fosOpinion.similar_decisions.slice(0, 5).map((decision, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <a 
                        href={`https://www.financial-ombudsman.org.uk/decision/${decision.decision_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {decision.decision_id || 'Unknown ID'}
                      </a>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`${getOutcomeColor(decision.outcome?.toLowerCase() || '')}`}
                        >
                          {decision.outcome || 'Unknown'}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {Math.round((decision.similarity_score || 0) * 100)}% similar
                        </span>
                      </div>
                    </div>
                    {(decision.interpretation || (decision as any).key_points) && (
                      <div className="text-xs text-gray-700">
                        {decision.interpretation ? (
                          <>
                            <div className="font-medium text-gray-800 mb-2">How this relates:</div>
                            <p className="text-sm text-gray-700 leading-relaxed mb-2">
                              {decision.interpretation}
                            </p>

                            {decision.key_factors && decision.key_factors.length > 0 && (
                              <div className="mt-3">
                                <div className="font-medium text-gray-800 text-xs mb-1">Key factors:</div>
                                <div className="flex flex-wrap gap-1">
                                  {decision.key_factors.map((factor: string, idx: number) => (
                                    <span
                                      key={idx}
                                      className="inline-block px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs"
                                    >
                                      {factor}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          // Fallback for old key_points format during transition
                          (decision as any).key_points && (decision as any).key_points.length > 0 && (
                            <>
                              <div className="font-medium text-gray-800 mb-1">Key points:</div>
                              <ul className="list-disc list-inside space-y-1">
                                {(decision as any).key_points.map((kp: string, idx: number) => {
                                  const point = kp.trim();
                                  if (!point) return null;
                                  const formattedPoint = point.match(/[.!?]$/) ? point : `${point}.`;
                                  return (
                                    <li key={idx} className="break-words">
                                      {formattedPoint}
                                    </li>
                                  );
                                }).filter(Boolean)}
                              </ul>
                            </>
                          )
                        )}
                      </div>
                    )}

                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-[11px] text-gray-500 pr-2 line-clamp-3">
                        { (decision as any).match_reasoning || 'Why relevant: matched by semantic similarity and precedent.' }
                      </div>
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={async () => {
                          try {
                            setLoadingDecision(true);
                            setOpenDecisionId(decision.decision_id);
                            const detail = await getOmbudsmanDecisionById(decision.decision_id);
                            setDecisionDetail(detail);
                          } catch (e) {
                            console.error('Failed to load decision detail', e);
                          } finally {
                            setLoadingDecision(false);
                          }
                        }}
                      >
                        View details
                      </button>
                    </div>
                    <div className="mt-1 text-[11px]">
                      <a
                        href={(decision as any).pdf_url || `https://www.financial-ombudsman.org.uk/decision/${decision.decision_id}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Open PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Metadata footer */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3" />
                <span>Generated <Timestamp value={fosOpinion.generated_at} format="absolute" fallback="Unknown" /></span>
              </div>
              {fosOpinion.processing_metadata && (
                <span>
                  Based on {fosOpinion.processing_metadata.decisions_analyzed || 0} similar decisions
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Disclaimer */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <strong>Disclaimer:</strong> This is an opinion generated based on historical FOS decisions. 
            It should be used as guidance only and does not guarantee actual FOS outcomes. Always consider 
            the specific circumstances of each case.
          </p>
        </div>
    </Panel>
    {openDecisionId && (
      <FOSDecisionDetailModal
        open={!!openDecisionId}
        onClose={() => { setOpenDecisionId(null); setDecisionDetail(null); }}
        decisionId={openDecisionId}
        loading={loadingDecision}
        decision={decisionDetail}
      />
    )}
  </>
  );
};

export default FOSOpinionPanel;
